import { useState } from 'react';
import { MagnifyingGlass, FunnelSimple, X } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * DataToolbar — sticky search + filter chip toolbar reused across list pages.
 *
 * Props:
 *   value, onChange — search string state
 *   filters: Array<{ key, label, value, options:[{value,label}] , onChange }>
 *   actions — optional React node placed on the far right (e.g. "+ Add" button)
 *   placeholder — search placeholder
 *   activeCount — number of active filters (for the chip ribbon)
 *   onClearAll — reset all filters / search
 *   testId
 */
export function DataToolbar({
  value,
  onChange,
  filters = [],
  actions,
  placeholder = 'Search…',
  onClearAll,
  testId = 'data-toolbar',
}) {
  const activeFilters = filters.filter(f => f.value && f.value !== 'all');
  const hasAnyState = !!value || activeFilters.length > 0;

  return (
    <div
      className="sticky top-0 z-30 -mx-1 px-1 py-3 bg-[#FCFAF6]/85 backdrop-blur supports-[backdrop-filter]:bg-[#FCFAF6]/70"
      data-testid={testId}
    >
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] flex flex-col">
        {/* Search row */}
        <div className="flex items-center gap-2 p-2 sm:p-2.5">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C] pointer-events-none" />
            <Input
              type="text"
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="pl-10 pr-10 border-0 bg-transparent rounded-xl focus-visible:ring-0 focus-visible:ring-offset-0 h-10 text-sm"
              data-testid={`${testId}-search`}
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            />
            {value && (
              <button
                onClick={() => onChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[#FCFAF6] hover:bg-[#EDE6D8] flex items-center justify-center"
                data-testid={`${testId}-clear-search`}
              >
                <X size={11} className="text-[#78716C]" />
              </button>
            )}
          </div>

          {/* Filter button (mobile-friendly) */}
          {filters.length > 0 && (
            <FilterPopover filters={filters} testId={testId} />
          )}

          {actions && <div className="shrink-0">{actions}</div>}
        </div>

        {/* Active filter chips */}
        {(activeFilters.length > 0 || hasAnyState) && (
          <div className="flex items-center flex-wrap gap-1.5 px-3 py-2 border-t border-[#FCFAF6]" data-testid={`${testId}-chips`}>
            {activeFilters.map((f) => {
              const opt = f.options.find(o => o.value === f.value);
              return (
                <button
                  key={f.key}
                  onClick={() => f.onChange('all')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0A2540]/10 text-[#0A2540] text-[11px] font-medium hover:bg-[#0A2540]/15 transition"
                  data-testid={`${testId}-chip-${f.key}`}
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {f.label}: <span className="font-semibold normal-case">{opt?.label || f.value}</span>
                  <X size={11} weight="bold" />
                </button>
              );
            })}
            {hasAnyState && (
              <button
                onClick={onClearAll}
                className="ml-auto text-[11px] text-[#78716C] hover:text-[#1C1917] transition"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                data-testid={`${testId}-clear-all`}
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPopover({ filters, testId }) {
  const [open, setOpen] = useState(false);
  const activeCount = filters.filter(f => f.value && f.value !== 'all').length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex items-center gap-1.5 px-3 h-10 rounded-xl border border-[#EDE6D8] hover:bg-[#FCFAF6] text-sm text-[#1C1917]"
          data-testid={`${testId}-filter-trigger`}
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          <FunnelSimple size={16} />
          Filters
          {activeCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#0A2540] text-white text-[10px] font-bold flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-3">
        {filters.map((f) => (
          <div key={f.key}>
            <label
              className="block text-[10px] uppercase tracking-wider text-[#78716C] font-semibold mb-1"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {f.label}
            </label>
            <select
              value={f.value || 'all'}
              onChange={(e) => f.onChange(e.target.value)}
              className="w-full h-9 rounded-lg border border-[#EDE6D8] px-2 text-sm bg-[#FFFFFF] focus:outline-none focus:border-[#0A2540]"
              data-testid={`${testId}-filter-${f.key}`}
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {f.options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
